// src/services/sociosService.js
import axios from 'axios';
import {
  collection,
  doc,
  updateDoc,
  serverTimestamp,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * ✅ Socios Service (Elion)
 * - Mantém suporte HTTP (backend local) quando disponível
 * - Faz fallback direto no Firestore quando o backend não estiver acessível
 * - Adiciona helper que a Central precisa: getSociosAceitosIds(uid)
 */

const SOCIOS_API = 'http://localhost:3000/socios';

/* =========================================================
   Helpers
========================================================= */

function parseAxiosError(error) {
  const msg =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    'Erro desconhecido';
  return String(msg);
}

/**
 * Tenta bater no backend. Se cair, retorna false e o caller faz fallback Firestore.
 */
async function tryPost(url, body) {
  try {
    await axios.post(url, body);
    return true;
  } catch (error) {
    // backend pode não estar rodando — fallback Firestore
    return false;
  }
}

/* =========================================================
   Ações (Aceitar / Rejeitar / Encerrar)
========================================================= */

/**
 * ✅ Aceitar convite de sociedade
 * id = docId na coleção 'socios'
 */
export const aceitarSociedade = async (id) => {
  if (!id) throw new Error('ID do convite ausente.');

  // 1) tenta backend
  const ok = await tryPost(`${SOCIOS_API}/aceitar`, { id });
  if (ok) return true;

  // 2) fallback Firestore
  try {
    await updateDoc(doc(db, 'socios', id), {
      status: 'accepted',
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Erro ao aceitar sociedade (Firestore):', error);
    throw new Error(parseAxiosError(error));
  }
};

/**
 * ❌ Rejeitar convite de sociedade
 */
export const rejeitarSociedade = async (id) => {
  if (!id) throw new Error('ID do convite ausente.');

  const ok = await tryPost(`${SOCIOS_API}/rejeitar`, { id });
  if (ok) return true;

  try {
    await updateDoc(doc(db, 'socios', id), {
      status: 'rejected',
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Erro ao rejeitar sociedade (Firestore):', error);
    throw new Error(parseAxiosError(error));
  }
};

/**
 * ⛔ Encerrar sociedade (status: ended)
 * (opcional, mas útil para sua regra de rede)
 */
export const encerrarSociedade = async (id) => {
  if (!id) throw new Error('ID da sociedade ausente.');

  const ok = await tryPost(`${SOCIOS_API}/encerrar`, { id });
  if (ok) return true;

  try {
    await updateDoc(doc(db, 'socios', id), {
      status: 'ended',
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Erro ao encerrar sociedade (Firestore):', error);
    throw new Error(parseAxiosError(error));
  }
};

/* =========================================================
   ✅ Central de Negócios — Sócios aceitos
========================================================= */

/**
 * ✅ Retorna UIDs dos sócios aceitos
 * - Coleção: socios { criador, convidado, status }
 * - Se uid é criador → sócio é convidado
 * - Se uid é convidado → sócio é criador
 */
export async function getSociosAceitosIds(uid) {
  if (!uid) return [];

  try {
    const q1 = query(
      collection(db, 'socios'),
      where('status', '==', 'accepted'),
      where('criador', '==', uid)
    );

    const q2 = query(
      collection(db, 'socios'),
      where('status', '==', 'accepted'),
      where('convidado', '==', uid)
    );

    const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

    const socios = [...snap1.docs, ...snap2.docs]
      .map((d) => d.data())
      .map((data) => (data.criador === uid ? data.convidado : data.criador))
      .filter(Boolean);

    // remove duplicados
    return Array.from(new Set(socios));
  } catch (error) {
    console.error('Erro ao buscar sócios aceitos:', error);
    return [];
  }
}
